import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { ComponentType, CSSProperties, SVGProps } from 'react';
import type { SvgComponent } from '@/types/SvgComponent';

// FPL icons forbid width/height/viewBox/ref and color via a CSS var, not `currentColor`.
type FplIconComponent = ComponentType<Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'viewBox' | 'ref'>>;

// The CSS custom property FPL icons read for their fill: `fill="var(--fpl-icon-color, var(--color-icon))"`.
export const FPL_ICON_COLOR_VAR = '--fpl-icon-color';
export const FPL_ICON_COLOR_SECONDARY_VAR = '--fpl-icon-color-2';
const FPL_ICON_COLOR = `var(${FPL_ICON_COLOR_VAR}, var(--color-icon))`;

/**
 * Wrap a Weave icon for the `fpl-migration` flag. Pass an FPL replacement when one exists.
 *
 * - Flag off: the exact legacy icon (renders its own `<title>` when a `title` is passed).
 * - Flag on + replacement: the FPL icon at its own size/color (legacy size/color ignored). The glyph
 *   is decorative (`aria-hidden`) per FPL's icon pattern, so `title` is dropped rather than mapped to a
 *   name — the accessible name belongs on the enclosing control (e.g. an `AppIconButton`'s `aria-label`).
 * - Flag on, no replacement: the legacy icon themed via the FPL color var (size kept).
 *
 * The wrapper is a plain component and does not forward a ref; ref-based anchors (e.g. a MUI tooltip
 * on the raw glyph) should wrap the icon in an element they can hold. Hard-coded `fill`/`stroke`
 * icons are unaffected by the color handling.
 */
export function fplIcon(Local: SvgComponent, Fpl?: FplIconComponent): SvgComponent {
  return function FplIcon(props) {
    const isFpl = useFplMigrationFlag();

    // Spread untouched so the SVG keeps its intrinsic width/height/viewBox (re-passing destructured
    // props would forward `undefined` and strip the viewBox, clipping non-24 icons).
    if (!isFpl) {
      return <Local {...props} />;
    }

    // No replacement: drive `currentColor` from the FPL var; ignore caller color, keep size.
    if (Fpl == null) {
      const { color: _color, style, ...rest } = props;
      const { color: _styleColor, ...restStyle } = style ?? {};
      const mergedStyle: CSSProperties = { color: FPL_ICON_COLOR, ...restStyle };
      return <Local style={mergedStyle} {...rest} />;
    }

    // Replacement: FPL glyphs are decorative, so drop legacy size/color (props and style) plus
    // title/viewBox/ref and mark the icon `aria-hidden`. The name is owned by the enclosing control.
    const {
      title: _title,
      color: _color,
      style,
      width: _width,
      height: _height,
      viewBox: _viewBox,
      ref: _ref,
      ...rest
    } = props;
    const { width: _styleWidth, height: _styleHeight, color: _styleColor, ...restStyle } = style ?? {};
    const mergedStyle = restStyle as CSSProperties;

    return <Fpl style={Object.keys(mergedStyle).length > 0 ? mergedStyle : undefined} {...rest} aria-hidden />;
  };
}
